//
//  describe-design-system.ts
//  Supernova CLI
//
//  Created by Jiri Trecak.
//  Copyright © Supernova.io. All rights reserved.
//

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports

import { Flags } from "@oclif/core"
import { SentryTraced } from "@sentry/nestjs"
import { z } from "zod"

import { commonFlags, SentryCommand } from "../types/index.js"
import { getWritableVersion } from "../utils/sdk.js"

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Definition

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Configuration

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Tool implementation

const DescribeDesignSystemConfigSchema = z
  .object({
    apiKey: z.string(),
    designSystemId: z.string(),
    proxyUrl: z.string().url().optional(),
  })
  .transform(data => ({
    ...data,
  }))

type DescribeDesignSystemConfig = z.infer<typeof DescribeDesignSystemConfigSchema>

/** Command that describes the structure of provided design system */
export class DescribeDesignSystem extends SentryCommand<DescribeDesignSystemConfig> {
  // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
  // MARK: - Command configuration

  // How this command can be run
  static override aliases = ["describe-design-system"]
  // Command help description
  static override description = "Describe structure of single design system by provided ID"
  // Examples how to use the command
  static override examples = [
    `$ @supernovaio/cli describe-design-system --apiKey="{xxx-xxx-xxx}" --designSystemId="{1234}"`,
  ]
  // Static flags to enable / disable features
  static override flags = {
    ...commonFlags,
    apiKey: Flags.string({ description: "API key to use for accessing Supernova instance", required: true }),
    designSystemId: Flags.string({ description: "Design System to describe structure of", required: true }),
    proxyUrl: Flags.string({
      description: "When set, CLI will use provided proxy URL for all requests",
      hidden: true,
      required: false,
    }),
  }

  get commandId(): string {
    return DescribeDesignSystem.id
  }

  get configSchema(): z.ZodType<DescribeDesignSystemConfig> {
    return DescribeDesignSystemConfigSchema
  }

  // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
  // MARK: - Command runtime

  @SentryTraced()
  async run(): Promise<void> {
    const { flags } = await this.parse(DescribeDesignSystem)

    // Get workspace -> design system –> version
    const { designSystem, id, instance } = await getWritableVersion(flags)

    // Get brands and themes
    const brands = await instance.brands.getBrands(id)
    const themes = await instance.tokens.getTokenThemes(id)

    this.log(`\n↳ Design system "${designSystem.name}", id: ${designSystem.id}`.cyan)
    for (const brand of brands) {
      this.log(`  ↳ Brand: "${brand.name}", id: ${brand.id}`)
      const brandThemes = themes.filter(t => t.brandId === brand.id)
      if (brandThemes.length > 0) {
        for (const theme of brandThemes) {
          this.log(`    ↳ Theme: "${theme.name}", id: ${theme.id}`)
        }
      } else {
        this.log(`    ↳ No themes defined in this brand`.gray)
      }
    }

    this.log("\nDone".green)
  }
}
